import React from 'react';
import type { TicketAttributeValues } from '../serverTypes';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { pillStyle, type SecondaryPillVariant } from './SecondaryAxisPills';
import { TICKET_ATTRIBUTES } from '../services/ticketAttributes';
import { type RowPillVisibility } from '../hooks/useRowPillVisibility';
import { attributeKindId } from '../services/rowPillKinds';

// ─── TicketAttributePills ───────────────────────────────
// <purpose>THE rendering of a ticket's classified-alongside ATTRIBUTES (wim vocabulary v7,
// 2026-09-13) — one info pill per bound attribute whose served face is a word. All three surfaces
// that state them mount THIS: the list row's line 2 (TicketRowMeta below), the detail header's meta
// line (TicketDetail) (the triage panel's chip row is gone, part 6). One component, because the
// interesting behaviour here is a SUPPRESSION — the tenant declares that the majority commodity and
// a private sender are not worth a pill — and a surface carrying its own copy of that rule is how
// two screens end up disagreeing about the same mail.</purpose>
// <dependencies>useCategoryMeta().attributeFace — the ONLY face source (its null means "render no
// pill"; see its resolver for the three states of the served faces map) · TICKET_ATTRIBUTES
// (services/ticketAttributes) for which attributes exist as ticket fields, and in which ORDER ·
// pillStyle('info', …) for the register.</dependencies>
// <constraints>SLOT ORDER IS FIXED and comes from the binding table, never from the data: a value
// arriving or resolving to nothing may not re-arrange the pills beside it (UI Layout Stability).
// The face is rendered VERBATIM — this is a documented exception to the info register's face rule
// (pillFace), and the only one whose reason is the tenant rather than the product: a served face is
// a spelling the tenant chose, and propercasing it would rewrite a word whose first letter is
// deliberately lowercase. The register's SHAPE still comes from the factory.
// No pill while cm.configLoading: with no vocabulary the resolver answers the propercased value by
// design ("nothing was declared"), so painting during the first /config fetch would show a face the
// settled row then removes — and because the common values are exactly the suppressed ones, that is
// the majority of rows, not an edge. A skeleton would reserve width for the same mostly-nothing, so
// the honest pre-settle state is empty (the standing loading posture's one exception: shimmer where
// something WILL land, nothing where it usually will not).</constraints>
export const TicketAttributePills: React.FC<{
  ticket: TicketAttributeValues;
  variant: SecondaryPillVariant;
  /**
   * The clerk's „Hinweise in der Liste" set (P2 Task R4, 2026-09-18) — passed by the LIST ROW and
   * by nobody else. It is a prop rather than a hook read inside this component precisely because
   * the preference is scoped to list rows: the detail header and the triage panel mount the same
   * pills and must keep stating every attribute, whatever a clerk hid from their list. Absent =
   * every attribute renders, which is what every pre-preference call site means.
   */
  visibility?: RowPillVisibility;
}> = ({ ticket, variant, visibility }) => {
  const cm = useCategoryMeta();
  if (cm.configLoading) return null;
  return (
    <>
      {TICKET_ATTRIBUTES.map(binding => {
        // A hidden attribute renders NOTHING — no pill, no slot. The toggle lives in Einstellungen,
        // so there is no on-screen transition to protect against (Layout-Stability), and a reserved
        // width would be permanent empty air on every row.
        if (visibility && !visibility.isVisible(attributeKindId(binding.name))) return null;
        // part 7, 2026-10-11: the sender kind is stated by the one SenderPill (row 3 / detail meta
        // line), never as a pill of its own — the binding stays for the filter and its facets.
        if (binding.name === 'sender_kind') return null;
        const value = ticket[binding.field];
        // Absent and null both mean "this row says nothing about it" — see TicketAttributeValues.
        if (!value) return null;
        const face = cm.attributeFace(binding.name, value);
        // The SERVED SUPPRESSION. Never `?? attributeValueStandIn(value)` here: that collapses the
        // tenant's decision into the "no decision" case and puts a pill on every row.
        if (face === null) return null;
        return (
          <span
            key={binding.name}
            data-testid={`attribute-pill-${binding.field}`}
            style={pillStyle('info', variant)}
          >
            {face}
          </span>
        );
      })}
    </>
  );
};
